import React from 'react';
import { Link } from 'react-router-dom';
import { BookOpenIcon, CalendarDaysIcon, CurrencyEuroIcon } from '@heroicons/react/24/outline';
import GeneralStatIndicatorPortlet from '@ui/portlet/GeneralStatIndicatorPortlet';
import Message from '@ui/message/Message';
import TablePlaceholder from '@ui/placeholder/TablePlaceholder';
import { CompanyHotelIntegrations, Integration, IntegrationType } from '@app/model/integration-settings/Integration';
import { useCompanyIntegrations } from '@app/model/integration-settings/service/IntegrationSettingsHooks';
import {
	findIntegration,
	formatDayTime,
	INTERVAL_LABEL,
	PROVIDER_LABEL,
	TYPE_LABEL,
	TYPE_ORDER,
} from './IntegrationSettings.utilities';
import { BackfillTag, IntegrationRunHistory, StatusTag } from './card/IntegrationHeaderActions';
import IntegrationSettingsAccess, { useIntegrationSettingsAllowed } from './IntegrationSettingsAccess';

const TYPE_ICON: Record<IntegrationType, React.ReactNode> = {
	AVAILABILITY: <CalendarDaysIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />,
	RESERVATIONS: <BookOpenIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />,
	PRICING: <CurrencyEuroIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />,
};

// The two lines under the status: the provider and the interval, then the last run, the backfill state or a link.
const cellLines = (integration: Integration): [string, string] => {
	const provider = integration.provider === null ? '—' : PROVIDER_LABEL[integration.provider];
	const interval = integration.interval === null ? '' : ` · ${INTERVAL_LABEL[integration.interval]}`;
	if (integration.status === 'NOT_SET_UP') return ['—', 'Set up'];
	if (integration.status === 'OFF') return [provider, 'Switched off'];
	if (integration.type === 'PRICING') {
		const push = integration.pricing?.lastPush ?? null;
		return [provider, push === null ? 'No price push yet' : `Last push ${formatDayTime(push.createdAt)}`];
	}
	const backfill = integration.backfill;
	if (backfill !== null && backfill.runningUntil != null) return [`${provider}${interval}`, 'Reservation import running'];
	if (backfill !== null && backfill.lockedUntil !== null) return [`${provider}${interval}`, `Backfill locked until ${formatDayTime(backfill.lockedUntil).slice(6)}`];
	if (backfill !== null && backfill.streams.every(stream => stream.doneParts === 0) && integration.status === 'ACTIVE') return [`${provider}${interval}`, 'Backfill not started'];
	if (integration.lastRunAt === null) return [`${provider}${interval}`, `First check ${formatDayTime(integration.nextRunAt)}`];
	return [`${provider}${interval}`, `Last run ${formatDayTime(integration.lastRunAt)}`];
};

const IntegrationCell: React.FunctionComponent<{ hotel: CompanyHotelIntegrations, type: IntegrationType }> =
	({ hotel, type }) => {
		const integration = findIntegration(hotel.integrations, type);
		if (integration === undefined) return <td className="px-3 py-3 align-top text-xs text-gray-400">—</td>;
		const [first, second] = cellLines(integration);
		return <td className="px-3 py-3 align-top text-xs">
			<Link to={`/secured/settings/integrations/${hotel.companyHotelId}#${type.toLowerCase()}`} className="block">
				<div className="flex flex-wrap items-center gap-2">
					<StatusTag integration={integration} />
					<IntegrationRunHistory integration={integration} />
					<BackfillTag integration={integration} />
				</div>
				<div className="mt-1.5 text-gray-700">{first}</div>
				<div className={integration.status === 'NOT_SET_UP' ? 'mt-0.5 font-semibold text-blue-600' : 'mt-0.5 text-gray-500'}>{second}</div>
			</Link>
		</td>;
	};

// Below 640 px: one card per property, the three types stacked (the table needs 640 px or more).
const IntegrationsList: React.FunctionComponent<{ hotels: CompanyHotelIntegrations[] }> = ({ hotels }) =>
	<ul className="mt-6 flex flex-col gap-y-4 sm:hidden">
		{hotels.map(hotel => <li key={hotel.companyHotelId} className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
			<Link to={`/secured/settings/integrations/${hotel.companyHotelId}`} className="text-sm font-semibold text-blue-600 hover:text-blue-500">
				{hotel.name} →
			</Link>
			<div className="mt-3 flex flex-col gap-y-3 divide-y divide-gray-100">
				{TYPE_ORDER.map(type => {
					const integration = findIntegration(hotel.integrations, type);
					if (integration === undefined) return null;
					const [first, second] = cellLines(integration);
					return <Link key={type} to={`/secured/settings/integrations/${hotel.companyHotelId}#${type.toLowerCase()}`} className="block pt-3 text-xs first:pt-0">
						<div className="flex flex-wrap items-center gap-2">
							<span className="w-24 shrink-0 font-semibold text-gray-900">{TYPE_LABEL[type]}</span>
							<StatusTag integration={integration} />
							<IntegrationRunHistory integration={integration} />
							<BackfillTag integration={integration} />
						</div>
						<div className="mt-1 text-gray-700">{first} · <span className={integration.status === 'NOT_SET_UP' ? 'font-semibold text-blue-600' : 'text-gray-500'}>{second}</span></div>
					</Link>;
				})}
			</div>
		</li>)}
	</ul>;

const countActive = (hotels: CompanyHotelIntegrations[], type: IntegrationType) =>
	hotels.filter(hotel => findIntegration(hotel.integrations, type)?.status === 'ACTIVE').length;

const IntegrationsTable: React.FunctionComponent<{ hotels: CompanyHotelIntegrations[] }> = ({ hotels }) => <>
	<div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
		{TYPE_ORDER.map(type => <GeneralStatIndicatorPortlet key={type} title={TYPE_LABEL[type]} icon={TYPE_ICON[type]}
																												stat={`${countActive(hotels, type)} of ${hotels.length} active`} />)}
	</div>
	<IntegrationsList hotels={hotels} />
	<div className="relative mt-6 hidden overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm sm:block">
		<table className="min-w-full divide-y divide-gray-300">
			<thead>
			<tr>
				<th scope="col" className="py-2.5 pl-4 pr-3 text-left text-xs font-semibold text-gray-900 sm:pl-6">Property </th>
				{TYPE_ORDER.map(type => <th key={type} scope="col" className="px-3 py-2.5 text-left text-xs font-semibold text-gray-900">
					{TYPE_LABEL[type]}
				</th>)}
			</tr>
			</thead>
			<tbody className="divide-y divide-gray-200">
			{hotels.map((hotel, index) => <tr key={hotel.companyHotelId}>
				<td className="py-3 pl-4 pr-3 align-top text-xs font-semibold sm:pl-6">
					<Link to={`/secured/settings/integrations/${hotel.companyHotelId}`} className="text-blue-600 hover:text-blue-500">
						{hotel.name} →
					</Link>
				</td>
				{TYPE_ORDER.map(type => <IntegrationCell key={type} hotel={hotel} type={type} />)}
			</tr>)}
			</tbody>
		</table>
	</div>
	<p className="mt-4 text-xs text-gray-500">
		One bar per run or price push, newest on the right, at most 14. Green: success. Amber: partly failed. Red: failed. Gray: waiting.
	</p>
</>;

const IntegrationsSettingsPage = React.memo(() => {
    const { isAllowed } = useIntegrationSettingsAllowed();
    const { data, isLoading, isError } = useCompanyIntegrations(isAllowed);

	return <IntegrationSettingsAccess>
		<div className="mx-auto max-w-2xl rounded-xl bg-white p-4 sm:p-10 lg:mx-0 lg:max-w-none">
			<div>
				<h1 className="text-base font-semibold leading-6 text-gray-900">Integrations </h1>
				<p className="text-sm leading-6 text-gray-500">Connect each property of your company to its PMS and its channel manager.</p>
			</div>
			<div className="mt-3 border-t border-gray-200 pt-6">
				{isLoading && <TablePlaceholder />}
				{isError && <Message state="error" title="The integrations did not load">Reload the page. If the problem stays, contact RoomPulse.</Message>}
				{data !== undefined && data.length === 0 && <Message state="info">Your company has no property yet.</Message>}
				{data !== undefined && data.length > 0 && <IntegrationsTable hotels={data} />}
			</div>
		</div>

	</IntegrationSettingsAccess>;
});

export default IntegrationsSettingsPage;
